import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import Modal from 'components/modules/modal/Modal';
import { updateLocAlim } from 'services/api-service/center';
import { utilService } from 'services/util-service';

interface IInfoData {
  isOpen: boolean;
  raiIdx?: string | number | null;
  shipName?: string;
  name?: string;
  phoneNumber?: string;
  lastReceivedTime?: string;
  lastLocLat?: string | number;
  lastLocLon?: string | number;
  alimType: string;
}

interface ModalAlertProps {
  info: IInfoData | undefined;
  onClose: () => void;
  onRegister: () => void;
}

const ModalAlert = ({ info, onClose, onRegister }: ModalAlertProps) => {
  const [isShow, setShow] = useState(false);
  const [localInfo, setLocalInfo] = useState<IInfoData | undefined>(undefined);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (info?.isOpen) {
      setShow(true);
      setLocalInfo(info);
    } else {
      setShow(false);
    }
  }, [info]);

  const handleClose = () => {
    setShow(false);
    onClose();
    setTimeout(() => {
      setLocalInfo(undefined);
    }, 300);
  };

  const handleRegister = async () => {
    if (!localInfo) return;

    try {
      const response: any = await updateLocAlim(
        localInfo.raiIdx ? String(localInfo.raiIdx) : undefined,
        undefined,
        localInfo.shipName || undefined,
        localInfo.name || undefined,
        localInfo.phoneNumber || '',
        localInfo.lastReceivedTime || '',
        localInfo.lastLocLat ? String(localInfo.lastLocLat) : '',
        localInfo.lastLocLon ? String(localInfo.lastLocLon) : '',
        '사고등록',
        localInfo.alimType
      );

      if (response.result === 'success') {
        handleClose();
        onRegister();
      }
    } catch (error) {
      console.error('해양사고 등록 실패:', error);
    }
  };

  const getMinutesOverTime = (lastTimeStr: string | undefined): string => {
    if (!lastTimeStr) return '0분';

    const lastTime = new Date(lastTimeStr);
    const now = new Date();
    const diffMs = now.getTime() - lastTime.getTime();
    const diffMinutes = Math.max(0, Math.floor(diffMs / (1000 * 60)));

    return `${diffMinutes}분`;
  };

  const calculatedOverTime = localInfo?.lastReceivedTime ? getMinutesOverTime(localInfo.lastReceivedTime) : '0분';

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const audio = new Audio('/sound/sos.wav');
      audio.loop = true;
      audioRef.current = audio;
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isShow) {
      audio.currentTime = 0;
      audio.play().catch((err) => {
        console.warn('Audio play blocked:', err);
      });
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [isShow]);

  return (
    <Modal isOpen={isShow} onClose={handleClose}>
      <ModalTemplate>
        <HeaderWrap>
          <HeaderTitle>위치 미수신 경고 ({calculatedOverTime} 초과)</HeaderTitle>
        </HeaderWrap>
        <ContentsWrap>
          <TableContainer>
            <TableTitleRow>선박정보</TableTitleRow>
            <TableRow>
              <TableData>{localInfo?.shipName || '선박명 없음'}</TableData>
            </TableRow>
            <TableRow>
              <TableData>
                {localInfo?.name || 'OOO'} ({utilService.formatPhoneNumber(localInfo?.phoneNumber ?? '')})
              </TableData>
            </TableRow>
            <TableRow>
              <TableData>마지막 수신 : {localInfo?.lastReceivedTime || '00시 00분 00초'}</TableData>
            </TableRow>
            <TableRow style={{ borderBottom: 'none' }}>
              <TableData>
                마지막 위치: {localInfo?.lastLocLat ?? '-'} / {localInfo?.lastLocLon ?? '-'}
              </TableData>
            </TableRow>
          </TableContainer>
        </ContentsWrap>
        <FooterWrap>
          <BlueButton onClick={handleClose}>닫기</BlueButton>
          <DarkBlueButton onClick={handleRegister}>해양사고 등록</DarkBlueButton>
        </FooterWrap>
      </ModalTemplate>
    </Modal>
  );
};

const ModalTemplate = styled.div`
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 360px;
  display: flex;
  flex-flow: column nowrap;
  background-color: #abc9df;
  border-radius: 10px;
  overflow: hidden;
`;
const HeaderWrap = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  background-color: #d1003b;
  height: 50px;
`;
const HeaderTitle = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
`;
const ContentsWrap = styled.div`
  padding: 24px 20px 16px 20px;
  display: flex;
  flex-flow: column nowrap;
  background-color: #abc9df;
`;
const TableContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;
const TableTitleRow = styled.div`
  background-color: #11497e;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  padding: 12px 16px;
  text-align: left;
`;
const TableRow = styled.div`
  background-color: #e9f3fb;
  border-bottom: 1px solid #abc9df;
  display: flex;
  align-items: center;
`;
const TableData = styled.div`
  padding: 14px 16px;
  color: #222222;
  font-size: 16px;
  font-weight: 500;
  text-align: left;
  width: 100%;
`;
const FooterWrap = styled.div`
  padding: 0 30px 20px 30px;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;
const BaseButton = styled.button`
  flex: 1;
  height: 40px;
  border: none;
  border-radius: 10px;
  color: #ffffff;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;
const BlueButton = styled(BaseButton)`
  background-color: #0071d8;
`;
const DarkBlueButton = styled(BaseButton)`
  background-color: #11497e;
`;

export default ModalAlert;
